import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            <h1 className="font-heading text-lg font-semibold">Användarvillkor</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 prose prose-slate">
        <p className="text-muted-foreground">Senast uppdaterad: Mars 2026</p>

        <h2>1. Godkännande av villkor</h2>
        <p>
          Genom att skapa ett konto eller använda Livon® ("Tjänsten") godkänner du dessa 
          användarvillkor. Om du inte godkänner villkoren får du inte använda Tjänsten.
        </p>

        <h2>2. Om Tjänsten och tjänsteleverantör</h2>
        <p>
          Livon är ett koncept och en digital tjänst som tillhandahålls och drivs av 
          <strong>Expressa AB</strong>, org.nr 556940-4436, med säte i Sverige.
        </p>
        <p>
          Alla köp, betalningar och avtal ingås med Expressa AB.
        </p>
        <p>
          Livon är en digital livsplanerare som hjälper dig organisera och förbättra olika 
          områden i ditt liv, inklusive hälsa, ekonomi, relationer och personlig utveckling.
        </p>

        <h2>3. Konto och registrering</h2>
        <ul>
          <li>Du ansvarar för att hålla dina inloggningsuppgifter säkra</li>
          <li>Du får inte dela ditt konto med andra</li>
          <li>Informationen du anger måste vara korrekt</li>
        </ul>

        <h2>4. Provperiod och betalning</h2>
        <ul>
          <li>Nya användare får <strong>3 dagars gratis provperiod</strong> med full tillgång till alla funktioner</li>
          <li>Under provperioden krävs ingen betalning</li>
          <li>Efter provperioden kostar Livon <strong>299 kr som engångsbetalning</strong> — ingen prenumeration, inga förnyelser</li>
          <li>När du har betalat har du <strong>full tillgång</strong> till alla funktioner — inga förnyelser krävs</li>
          <li>Om du inte betalar efter provperioden avslutas tillgången till premiumfunktioner</li>
        </ul>

        <h2>5. Ångerrätt</h2>
        <p>
          Du har <strong>3 dagars gratis provperiod</strong> för att testa alla funktioner innan betalning.
          Eftersom provperioden ger dig full möjlighet att utvärdera tjänsten innan du väljer att betala,
          gäller inte distansavtalslagens ångerrätt efter att du aktivt valt att slutföra köpet.
          Vid frågor eller önskemål om återbetalning, kontakta oss på support@livon.se så ser vi över ditt ärende individuellt.
        </p>
        <p>
          Har du frågor? Kontakta oss på support@livon.se
        </p>

        <h2>6. Tillåten användning</h2>
        <p>Du får använda Tjänsten för personligt bruk. Du får INTE:</p>
        <ul>
          <li>Kopiera, modifiera eller distribuera Tjänsten</li>
          <li>Försöka hacka eller manipulera Tjänsten</li>
          <li>Använda Tjänsten för olagliga ändamål</li>
          <li>Dela innehåll som är olagligt, stötande eller kränkande</li>
          <li>Skapa flera konton för att utnyttja provperioder</li>
        </ul>

        <h2>7. Immateriella rättigheter</h2>
        <p>
          Livon® är ett registrerat varumärke (reg.nr 640914) som tillhör Expressa AB. 
          Allt innehåll (design, kod, texter, bilder) tillhör Expressa AB och skyddas 
          av upphovsrätt och varumärkesrätt. Du får inte kopiera eller använda materialet utan skriftligt tillstånd.
        </p>

        <h2>8. Ditt innehåll</h2>
        <p>
          Du behåller äganderätten till allt innehåll du skapar i Tjänsten (anteckningar, mål, etc). 
          Du ger oss rätt att lagra och visa detta innehåll för dig inom Tjänsten.
        </p>

        <h2>9. Tillgänglighet</h2>
        <p>
          Vi strävar efter att Tjänsten ska vara tillgänglig dygnet runt, men kan inte garantera 
          detta. Planerat underhåll meddelas i förväg när det är möjligt.
        </p>

        <h2>10. Ansvarsbegränsning</h2>
        <p>
          Livon tillhandahålls "i befintligt skick". Vi garanterar inte att Tjänsten är felfri 
          eller att den uppfyller alla dina behov.
        </p>
        <p>
          <strong>Viktigt:</strong> Livon ersätter inte professionell rådgivning inom hälsa, 
          ekonomi eller juridik. Konsultera alltid relevanta experter för sådana frågor.
        </p>

        <h2>11. Uppsägning</h2>
        <p>
          Du kan när som helst avsluta ditt konto via Inställningar. Vi kan stänga av eller 
          avsluta ditt konto om du bryter mot dessa villkor.
        </p>

        <h2>12. Ändringar av villkoren</h2>
        <p>
          Vi kan uppdatera dessa villkor. Väsentliga ändringar meddelas minst 30 dagar i förväg. 
          Fortsatt användning efter ändringar innebär att du godkänner de nya villkoren.
        </p>

        <h2>13. Tillämplig lag</h2>
        <p>
          Dessa villkor regleras av svensk lag. Tvister avgörs av svensk domstol.
        </p>

        <h2>14. Kontakt</h2>
        <p>
          Har du frågor om dessa villkor?<br />
          E-post: support@livon.se
        </p>

        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-300 m-0">
            <strong>Sammanfattning:</strong> 3 dagars gratis provperiod, sedan 299 kr som engångsbetalning för full tillgång. Inga förnyelser, inga månadsavgifter.
          </p>
        </div>
      </main>
    </div>
  );
}
